/**
 * 전역 인증 상태 — 앱 전체에서 useAuth() 로 현재 사용자 정보 접근.
 *
 *   const { user, status, refresh, signOut } = useAuth();
 *
 * 동작:
 *   - 앱 마운트 시 GET /auth/me 1회 호출하여 세션 복원
 *   - 401 응답이면 user=null (미로그인)
 *   - login 성공 시 setUser 로 즉시 반영, 또는 refresh() 로 다시 가져오기
 */

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { ApiCallError } from './api';
import * as authApi from './auth-api';
import { queryClient } from './queryClient';
import type { AuthUser } from './types';

type AuthStatus = 'loading' | 'authenticated' | 'guest';

interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  setUser: (u: AuthUser | null) => void;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [status, setStatus] = useState<AuthStatus>('loading');

  const refresh = useCallback(async () => {
    try {
      const { user } = await authApi.me();
      setUser(user);
      setStatus('authenticated');
    } catch (e) {
      if (e instanceof ApiCallError && e.status === 401) {
        setUser(null);
        setStatus('guest');
        return;
      }
      // 네트워크 오류 등 — guest 로 간주 (재시도는 사용자가 새로고침)
      setUser(null);
      setStatus('guest');
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const signOut = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // 이미 만료된 세션 등 — 무시하고 클라이언트 상태만 정리
    }
    setUser(null);
    setStatus('guest');
    // ⭐조회 캐시도 비운다 (v2.127.0) — 같은 탭에서 다른 사람이 로그인하면 1분 동안
    //   앞사람의 결재 배지·홈 타일 숫자가 그대로 보였다(viewer 홈에 「판보 2」). 숫자 하나지만 남의 것이다.
    queryClient.clear();
  }, []);

  const handleSetUser = useCallback((u: AuthUser | null) => {
    // 로그인(사람이 바뀔 수 있는 순간)에도 비운다 — 로그아웃을 안 거치고 세션이 만료된 뒤 다시 로그인하는 경우
    queryClient.clear();
    setUser(u);
    setStatus(u ? 'authenticated' : 'guest');
  }, []);

  return (
    <AuthContext.Provider
      value={{ status, user, setUser: handleSetUser, refresh, signOut }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth 는 <AuthProvider> 내부에서만 호출하세요');
  return ctx;
}
